// region API interfaces
/** An object representing a single chart, when getting the list of charts from the API */
export interface ChartListItem {
  id: number;
  chart_name: string;
}

/** A chart which has been saved to the API */
export interface SavedChart {
  id: number;
  user_id: number;
  chart_name: string;
  chart_object: ChartData;
}
// endregion

/** Generic chart interface, for specifically setting series and data source types allowed */
export interface BaseChart<S extends BaseSeries<BaseDataSource>> {
  version: string;
  title: string;
  subtitle?: string;
  colorPalette?: ColorPalette;
  xAxes: XAxis[];
  yAxes: YAxis[];
  series: S[];
  plotLines: ReferenceLine[];
}

/** An AI Hub chart object with any combination of series and data source types */
export interface ChartData extends BaseChart<BaseSeries<DataSource>> {}

/** A chart with only raw data sources */
export interface RawChart
  extends BaseChart<LineSeries<RawDataSource> | ScatterSeries<RawDataSource>> {}

export type ColorPalette = string[];

//region Axis Interfaces
/** An axis of a chart */
export interface Axis {
  /** The uuid of the axis */
  id: string;
  type: "linear" | "datetime";
  alignment: string;
  title: {
    text: string;
    enabled: boolean;
  };
  min?: string | number | null;
  max?: string | number | null;
}

/** An X Axis of a chart */
export interface XAxis extends Axis {
  alignment: "bottom" | "top";
  min?: string;
  max?: string;
}

/** A Y Axis of a chart */
export interface YAxis extends Axis {
  alignment: "left" | "right";
  min?: number | null;
  max?: number | null;
  tagId?: number | null;
}

export interface ReferenceLine {
  id: string;
  targetAxis: string | null;
  title?: string;
  color?: string;
  dashStyle?: string;
  value: string | number | Date | null;
  width?: number;
}
//endregion

//region Series Interfaces
/** A Series of a chart */
export interface BaseSeries<DataSource extends BaseDataSource> {
  id: string;
  /** The uuid of the parent series, only present if this is a child series */
  parentId?: string;
  seriesType: "line" | "scatter" | "range";
  title: string;
  /** The uuid of the x-axis this series is using */
  xAxisId: string;
  /** The uuid of the y-axis this series is using */
  yAxisId: string;
  dataSource: DataSource;
  colorIndex: number;
  /** Overrides the color index if present */
  color?: string;
}

export type Series<T extends BaseDataSource> =
  | LineSeries<T>
  | ScatterSeries<T>
  | RangeSeries<T>;

export type PointShape =
  | "triangle"
  | "triangle-down"
  | "diamond"
  | "square"
  | "circle";

export interface PointSeries<DataSource extends BaseDataSource>
  extends BaseSeries<DataSource> {
  pointWidth?: number;
  pointShape?: PointShape;
}

/** A Line series */
export interface LineSeries<DataSource extends BaseDataSource>
  extends PointSeries<DataSource> {
  seriesType: "line";
  lineWidth?: number;
  pointsEnabled?: boolean;
}

export interface RangeSeries<DataSource extends BaseDataSource>
  extends BaseSeries<DataSource> {
  seriesType: "range";
}

/** A Scatter series */
export interface ScatterSeries<DataSource extends BaseDataSource>
  extends PointSeries<DataSource> {
  seriesType: "scatter";
}
//endregion

//region Data Source Interfaces
/** Base data source interface */
export interface BaseDataSource {
  type: string;
  args: Object;
}

export type NonRawDatasource =
  | ScadaDatetimeTagDataSource
  | ScadaTagTagDataSource
  | ScadaMedianDataSource
  | ScadaRangeDataSource;

export type DataSource = NonRawDatasource | RawDataSource;

export type RawData = [number, number][] | [number, number, number][];

/** A data source that already contains the data to be displayed on the chart */
export interface RawDataSource extends BaseDataSource {
  type: "raw";
  args: {
    data: RawData;
  };
}

export interface ScadaDatetimeTagArgs {
  machineId: number | null;
  tagId: number | null;
  filters?: object;
}

/** A data source that will resolve into scada data in the form of datetime vs tag for a particular machine */
export interface ScadaDatetimeTagDataSource extends BaseDataSource {
  type: "scada-datetime-tag";
  args: ScadaDatetimeTagArgs;
}

/** A data source that will resolve into scada data in the form of tag vs tag for a particular machine */
export interface ScadaTagTagDataSource extends BaseDataSource {
  type: "scada-tag-tag";
  args: {
    machineId: number;
    tagXId: number;
    tagYId: number;
    filters?: Object;
  };
}

export interface ScadaMedianDataSource extends BaseDataSource {
  type: "scada-median";
  args: {
    siteId: number;
    tagId: number;
  };
}

export interface ScadaRangeDataSource extends BaseDataSource {
  type: "scada-range";
  args: {
    siteId: number;
    tagId: number;
  };
}
//endregion

//region helper functions
export const isScadaDateTimeTagDataSource = (
  dataSource: DataSource
): dataSource is ScadaDatetimeTagDataSource => {
  return dataSource.type === "scada-datetime-tag";
};

/**
 * Returns the index of the next available colour in the colour palette.
 * @param series The existing series in the chart.
 * @param numberOfColors The number of colours in the colour palette.
 * @returns The index in the color palette.
 */
export const getColorIndex = (
  series: BaseSeries<BaseDataSource>[],
  numberOfColors: number
): number => {
  const existingColorIndexes = series.map(series => series.colorIndex);
  let colorIndex = 0;
  for (let i = 0; i <= numberOfColors - 1; i++) {
    if (!existingColorIndexes.includes(i)) {
      colorIndex = i;
      break;
    }
  }

  return colorIndex;
};
//endregion
